import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import StudentAttendanceView from '../../components/Attendance/StudentAttendanceView';
import { reportsAPI } from '../../services/reports';
import { useAuth } from '../../contexts/AuthContext';
import { useSupervisorTeacherPicker } from '../../hooks/useSupervisorTeacherPicker';
import HealthStatusChip from '../../components/Reports/HealthStatusChip';
import PunctualityStatusChip from '../../components/Reports/PunctualityStatusChip';
import ReportsFilterBar from '../../components/Reports/ReportsFilterBar';
import {
  buildWeeklyReportParams,
  filterByTeacher,
  filterClassStartByTeacher,
  filterByHealthStatus,
  filterByPunctualityStatus,
  summarizeBatchHealth,
  summarizeClassStart,
  weekMondayForDate,
  weekEndForStart,
  isDateInWeek,
} from '../../utils/reportFilters';

function WeekPicker({ weekStart, weekEnd, onPrev, onNext }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <IconButton onClick={onPrev} size="small" aria-label="Previous week">
        <ChevronLeftIcon />
      </IconButton>
      <Typography variant="body2" fontWeight={600}>
        Week: {weekStart} → {weekEnd} (IST)
      </Typography>
      <IconButton onClick={onNext} size="small" aria-label="Next week">
        <ChevronRightIcon />
      </IconButton>
    </Stack>
  );
}

function PendingTable({ title, rows, emptyText }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {title} ({rows.length})
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Teacher</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.sessionId}-${title}`}>
                  <TableCell>{r.sessionDate}</TableCell>
                  <TableCell>{r.batchName}</TableCell>
                  <TableCell>{r.teacherName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

function ClassStartPunctualityTab({ weekStart, setWeekStart, filterDate, filterTeacherId, filterStatus }) {
  const params = buildWeeklyReportParams(weekStart, filterDate);
  const { data, isLoading, error } = useQuery(
    ['reportClassStart', weekStart, filterDate],
    async () => {
      const res = await reportsAPI.getWeeklyClassStartPunctuality(params);
      return res.data?.data;
    },
    { keepPreviousData: true }
  );

  if (isLoading && !data) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">Failed to load class start report</Alert>;

  const onPrev = () => setWeekStart(data.previousWeekStart);
  const onNext = () => setWeekStart(data.nextWeekStart);

  const items = filterByPunctualityStatus(
    filterClassStartByTeacher(data.items || [], filterTeacherId),
    filterStatus
  );
  const summary = summarizeClassStart(items);

  return (
    <Box>
      <WeekPicker weekStart={data.weekStart} weekEnd={data.weekEnd} onPrev={onPrev} onNext={onNext} />
      {filterDate ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Showing sessions on {filterDate} (within selected week).
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Expected start uses each teacher&apos;s timezone. Join time is recorded when the teacher taps
        &quot;Start class&quot; (server time, UTC). Covered classes show the cover teacher and the original
        batch teacher. Secondary teachers appear as a separate <strong>Secondary</strong> row when assigned.
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <SummaryCard label="Sessions" value={summary.total} />
        <SummaryCard label="On time (5+ min early)" value={summary.green} status="good" />
        <SummaryCard label="Borderline" value={summary.amber} status="needs_attention" />
        <SummaryCard label="Late / on time" value={summary.red} status="problem" />
        <SummaryCard label="Not started" value={summary.notStarted} />
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Teacher</TableCell>
              <TableCell>Expected start</TableCell>
              <TableCell>Teacher joined</TableCell>
              <TableCell>Diff (min)</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.rowKey || `${row.sessionId}-${row.isSecondary ? 'sec' : 'pri'}`} hover>
                <TableCell>{row.sessionDate}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{row.batchName}</TableCell>
                <TableCell>{row.teacherDisplayName || row.teacherName}</TableCell>
                <TableCell>
                  {row.expectedStartDisplay || '—'}
                  {row.teacherTimezone ? (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {row.teacherTimezone}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{row.teacherJoinedDisplay || '—'}</TableCell>
                <TableCell>
                  {row.minutesDiff === null || row.minutesDiff === undefined ? '—' : row.minutesDiff}
                </TableCell>
                <TableCell>
                  <PunctualityStatusChip status={row.punctualityStatus} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function BatchHealthTab({ weekStart, setWeekStart, filterDate, filterTeacherId, filterStatus }) {
  const params = buildWeeklyReportParams(weekStart, filterDate);
  const { data, isLoading, error } = useQuery(
    ['reportBatchHealth', weekStart, filterDate],
    async () => {
      const res = await reportsAPI.getWeeklyBatchHealth(params);
      return res.data?.data;
    },
    { keepPreviousData: true }
  );

  if (isLoading && !data) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">Failed to load batch health</Alert>;

  const onPrev = () => setWeekStart(data.previousWeekStart);
  const onNext = () => setWeekStart(data.nextWeekStart);

  const batches = filterByHealthStatus(
    filterByTeacher(data.batches || [], filterTeacherId),
    filterStatus
  );
  const summary = summarizeBatchHealth(batches);

  return (
    <Box>
      <WeekPicker
        weekStart={data.weekStart}
        weekEnd={data.weekEnd}
        onPrev={onPrev}
        onNext={onNext}
      />
      {filterDate ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Metrics for {filterDate} only (within selected week).
        </Typography>
      ) : null}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <SummaryCard label="Batches" value={summary.total} />
        <SummaryCard label="Good" value={summary.good} status="good" />
        <SummaryCard label="Needs attention" value={summary.needsAttention} status="needs_attention" />
        <SummaryCard label="Problem" value={summary.problem} status="problem" />
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Batch</TableCell>
              <TableCell>Teacher</TableCell>
              <TableCell>Students</TableCell>
              <TableCell>Classes this week</TableCell>
              <TableCell>Work complete</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action needed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map((b) => (
              <TableRow key={b.batchId} hover>
                <TableCell sx={{ fontWeight: 700 }}>{b.batchName}</TableCell>
                <TableCell>{b.teacherName}</TableCell>
                <TableCell>{b.studentCount}</TableCell>
                <TableCell>{b.classesLabel}</TableCell>
                <TableCell>{b.workCompleteLabel}</TableCell>
                <TableCell>
                  <HealthStatusChip status={b.status} />
                </TableCell>
                <TableCell>{b.actionNeeded}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function TeacherHealthTab({ weekStart, setWeekStart, filterDate, filterTeacherId }) {
  const [detailId, setDetailId] = useState(null);
  const params = buildWeeklyReportParams(weekStart, filterDate);

  const { data, isLoading, error } = useQuery(
    ['reportTeacherHealth', weekStart, filterDate],
    async () => {
      const res = await reportsAPI.getWeeklyTeacherHealth(params);
      return res.data?.data;
    },
    { keepPreviousData: true }
  );

  const detailQuery = useQuery(
    ['reportTeacherDetail', detailId, weekStart, filterDate],
    async () => {
      const res = await reportsAPI.getWeeklyTeacherDetail(detailId, params);
      return res.data?.data;
    },
    { enabled: !!detailId }
  );

  if (isLoading && !data) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">Failed to load teacher health</Alert>;

  const teachers = filterByTeacher(data.teachers || [], filterTeacherId);

  return (
    <Box>
      <WeekPicker
        weekStart={data.weekStart}
        weekEnd={data.weekEnd}
        onPrev={() => setWeekStart(data.previousWeekStart)}
        onNext={() => setWeekStart(data.nextWeekStart)}
      />
      {filterDate ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Metrics for {filterDate} only (within selected week).
        </Typography>
      ) : null}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Teacher</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action needed</TableCell>
              <TableCell align="right">Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teachers.map((t) => (
              <TableRow key={t.teacherId} hover>
                <TableCell sx={{ fontWeight: 600 }}>{t.teacherName}</TableCell>
                <TableCell>
                  <HealthStatusChip status={t.status} />
                </TableCell>
                <TableCell>{t.actionNeeded}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setDetailId(t.teacherId)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(detailId)} onClose={() => setDetailId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detailQuery.data?.teacher?.name || 'Teacher'}</DialogTitle>
        <DialogContent>
          {detailQuery.isLoading ? (
            <CircularProgress size={24} />
          ) : detailQuery.data ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Batches: {detailQuery.data.summary?.batchCount}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Sessions this week: {detailQuery.data.summary?.sessionsThisWeek}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Attendance updated: {detailQuery.data.summary?.attendanceUpdated}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Homework sent: {detailQuery.data.summary?.homeworkSent}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Class notes completed: {detailQuery.data.summary?.classNotesCompleted}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Next class plan completed: {detailQuery.data.summary?.nextPlanCompleted}
              </Typography>
              <HealthStatusChip status={detailQuery.data.summary?.status} />
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function PendingItemsTab({ weekStart, setWeekStart, filterDate }) {
  const params = buildWeeklyReportParams(weekStart, filterDate);
  const { data, isLoading, error } = useQuery(
    ['reportPending', weekStart, filterDate],
    async () => {
      const res = await reportsAPI.getWeeklyPendingItems(params);
      return res.data?.data;
    },
    { keepPreviousData: true }
  );

  if (isLoading && !data) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">Failed to load pending items</Alert>;

  return (
    <Box>
      <WeekPicker
        weekStart={data.weekStart}
        weekEnd={data.weekEnd}
        onPrev={() => setWeekStart(data.previousWeekStart)}
        onNext={() => setWeekStart(data.nextWeekStart)}
      />
      {filterDate ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Pending items on {filterDate} only.
        </Typography>
      ) : null}
      <PendingTable title="Attendance missing" rows={data.pending?.attendance || []} emptyText="None — great!" />
      <PendingTable title="Homework missing" rows={data.pending?.homework || []} emptyText="None — great!" />
      <PendingTable title="Class notes missing" rows={data.pending?.notes || []} emptyText="None — great!" />
      <PendingTable title="Next class plan missing" rows={data.pending?.nextPlan || []} emptyText="None — great!" />
    </Box>
  );
}

function SummaryCard({ label, value, status }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 120 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h5" fontWeight={700}>
            {value ?? '—'}
          </Typography>
          {status ? <HealthStatusChip status={status} showEmoji={false} /> : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

function MonthlySummaryTab({ month, setMonth }) {
  const { data, isLoading, error } = useQuery(
    ['reportMonthlySummary', month],
    async () => {
      const res = await reportsAPI.getMonthlySummary({ month });
      return res.data?.data;
    },
    { keepPreviousData: true }
  );

  if (isLoading && !data) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">Failed to load monthly summary</Alert>;

  const shiftMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => shiftMonth(-1)} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="body2" fontWeight={600}>
          Month: {data.month} (IST)
        </Typography>
        <IconButton onClick={() => shiftMonth(1)} size="small">
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      <Typography variant="h6" gutterBottom>
        Teaching
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="Classes conducted" value={data.teaching?.totalClassesConducted} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="Students taught" value={data.teaching?.totalStudentsTaught} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard label="Active batches" value={data.teaching?.totalActiveBatches} />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Attendance
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Overall attendance rate: {data.attendance?.attendanceRate != null ? `${data.attendance.attendanceRate}%` : '—'}
      </Typography>
      {(data.attendance?.batchesBelow75 || []).length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Batches below 75% attendance:{' '}
          {data.attendance.batchesBelow75.map((b) => `${b.batchName} (${b.attendanceRate}%)`).join(', ')}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No batches below 75% attendance.
        </Typography>
      )}

      <Typography variant="h6" gutterBottom>
        Teachers
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Doing great ({data.teachers?.doingGreat?.length || 0})
            </Typography>
            {(data.teachers?.doingGreat || []).slice(0, 8).map((t) => (
              <Typography key={t.teacherId} variant="body2">
                {t.teacherName}
              </Typography>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Needing attention ({data.teachers?.needingAttention?.length || 0})
            </Typography>
            {(data.teachers?.needingAttention || []).slice(0, 8).map((t) => (
              <Typography key={t.teacherId} variant="body2">
                {t.teacherName} — {t.actionNeeded}
              </Typography>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Demos & trials
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <SummaryCard label="Demo classes" value={data.demos?.demoClasses} />
        <SummaryCard label="Trial classes" value={data.demos?.trialClasses} />
        <SummaryCard label="Completed / confirmed" value={data.demos?.completedOrConfirmed} />
      </Stack>
    </Box>
  );
}

const BUCKET_COLORS = {
  lt2: '#c62828',
  m2to3: '#ef6c00',
  m3to6: '#f9a825',
  m6to12: '#2e7d32',
  m12plus: '#1565c0',
};

const BUCKET_ORDER = ['lt2', 'm2to3', 'm3to6', 'm6to12', 'm12plus'];

function formatPct(value) {
  return value == null ? '—' : `${value}%`;
}

function TenureBar({ buckets, total }) {
  const safeTotal = total || BUCKET_ORDER.reduce((s, k) => s + (buckets?.[k] || 0), 0);
  if (!safeTotal) {
    return (
      <Typography variant="caption" color="text.secondary">
        No active students
      </Typography>
    );
  }
  return (
    <Box sx={{ minWidth: 140 }}>
      <Stack direction="row" sx={{ height: 10, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
        {BUCKET_ORDER.map((key) => {
          const count = buckets?.[key] || 0;
          if (!count) return null;
          return (
            <Box
              key={key}
              title={`${key}: ${count}`}
              sx={{
                width: `${(count / safeTotal) * 100}%`,
                bgcolor: BUCKET_COLORS[key],
              }}
            />
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {BUCKET_ORDER.map((k) => buckets?.[k] || 0).join(' / ')}
      </Typography>
    </Box>
  );
}

function monthToQuarter(month) {
  if (!month) return '';
  const [y, m] = month.split('-').map(Number);
  return `${y}-Q${Math.ceil(m / 3)}`;
}

function shiftQuarter(quarter, delta) {
  const match = String(quarter || '').match(/^(\d{4})-Q([1-4])$/i);
  if (!match) return quarter;
  let y = Number(match[1]);
  let q = Number(match[2]) + delta;
  while (q < 1) {
    q += 4;
    y -= 1;
  }
  while (q > 4) {
    q -= 4;
    y += 1;
  }
  return `${y}-Q${q}`;
}

function leaveReasonLabel(reason) {
  const map = {
    dropped: 'Dropped',
    completed: 'Completed',
    deactivated: 'Deactivated',
    subscription_cancelled: 'Sub cancelled',
  };
  return map[reason] || reason || '—';
}

function TeacherRetentionTab({ month, setMonth }) {
  const [mode, setMode] = useState('snapshot');
  const [periodType, setPeriodType] = useState('month');
  const [quarter, setQuarter] = useState(() => monthToQuarter(month));
  const [detailTeacherId, setDetailTeacherId] = useState(null);

  React.useEffect(() => {
    if (month) setQuarter(monthToQuarter(month));
  }, [month]);

  const queryParams = {
    mode,
    periodType,
    ...(periodType === 'month' ? { month } : { quarter }),
  };

  const { data, isLoading, error } = useQuery(
    ['reportTeacherRetention', mode, periodType, month, quarter],
    async () => {
      const res = await reportsAPI.getMonthlyTeacherRetention(queryParams);
      return res.data?.data;
    },
    { keepPreviousData: true }
  );

  const detailQuery = useQuery(
    ['reportTeacherRetentionDetail', detailTeacherId, mode, periodType, month, quarter],
    async () => {
      const res = await reportsAPI.getMonthlyTeacherRetentionDetail(detailTeacherId, queryParams);
      return res.data?.data;
    },
    { enabled: !!detailTeacherId, keepPreviousData: true }
  );

  const shiftMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  if (isLoading && !data) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">Failed to load teacher retention</Alert>;

  const summary = data?.summary || {};
  const showPeriodNav = mode !== 'snapshot';

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, v) => v && setMode(v)}
        >
          <ToggleButton value="snapshot">Snapshot</ToggleButton>
          <ToggleButton value="period">Period</ToggleButton>
          <ToggleButton value="cohort">Cohort</ToggleButton>
        </ToggleButtonGroup>

        {showPeriodNav ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={periodType}
            onChange={(_, v) => v && setPeriodType(v)}
          >
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="quarter">Quarter</ToggleButton>
          </ToggleButtonGroup>
        ) : null}

        {showPeriodNav && periodType === 'month' ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => shiftMonth(-1)} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" fontWeight={600}>
              Month: {month} (IST)
            </Typography>
            <IconButton onClick={() => shiftMonth(1)} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        ) : null}

        {showPeriodNav && periodType === 'quarter' ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => setQuarter((q) => shiftQuarter(q, -1))} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" fontWeight={600}>
              Quarter: {quarter} (IST)
            </Typography>
            <IconButton onClick={() => setQuarter((q) => shiftQuarter(q, 1))} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        ) : null}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Primary-teacher tenure. Left = dropped, completed, deactivated, or subscription cancelled.
        {mode === 'snapshot' ? ' Showing all-time snapshot.' : null}
        {mode === 'period' ? ' Period counts joined/left in the selected window.' : null}
        {mode === 'cohort' ? ' Cohort = students who joined in the selected window.' : null}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryCard label="Active students" value={summary.activeStudents} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryCard label="Early churn" value={formatPct(summary.earlyChurnRate)} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryCard label="3m retention" value={formatPct(summary.retention3m)} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryCard label="6m retention" value={formatPct(summary.retention6m)} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryCard label="Avg tenure (mo)" value={summary.avgTenureMonths ?? '—'} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SummaryCard label="Median tenure" value={summary.medianTenureMonths ?? '—'} />
        </Grid>
      </Grid>

      {mode === 'period' && summary.period ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          In period: joined {summary.period.joined}, left {summary.period.left}
          {summary.period.leaveReasons
            ? ` (dropped ${summary.period.leaveReasons.dropped}, completed ${summary.period.leaveReasons.completed}, deactivated ${summary.period.leaveReasons.deactivated}, sub cancelled ${summary.period.leaveReasons.subscription_cancelled})`
            : ''}
        </Alert>
      ) : null}

      {mode === 'cohort' && summary.cohort ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cohort size {summary.cohort.size}: still active {summary.cohort.stillActive} (
          {formatPct(summary.cohort.stillActiveRate)}), early exits {summary.cohort.earlyExitCount}, 3m retention{' '}
          {formatPct(summary.cohort.retention3m)}, 6m retention {formatPct(summary.cohort.retention6m)}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        {BUCKET_ORDER.map((key) => (
          <Chip
            key={key}
            size="small"
            label={(data?.buckets || []).find((b) => b.key === key)?.label || key}
            sx={{ bgcolor: BUCKET_COLORS[key], color: '#fff' }}
          />
        ))}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Teacher</TableCell>
              <TableCell align="right">Active</TableCell>
              <TableCell>Active tenure mix</TableCell>
              <TableCell align="right">Early churn</TableCell>
              <TableCell align="right">3m ret.</TableCell>
              <TableCell align="right">6m ret.</TableCell>
              {mode === 'period' ? <TableCell align="right">Joined / Left</TableCell> : null}
              {mode === 'cohort' ? <TableCell align="right">Cohort active</TableCell> : null}
              <TableCell align="right">Avg tenure</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.teachers || []).map((t) => (
              <TableRow
                key={t.teacherId}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => setDetailTeacherId(t.teacherId)}
              >
                <TableCell>{t.teacherName}</TableCell>
                <TableCell align="right">{t.activeCount}</TableCell>
                <TableCell>
                  <TenureBar buckets={t.activeBuckets} total={t.activeCount} />
                </TableCell>
                <TableCell align="right">{formatPct(t.earlyChurnRate)}</TableCell>
                <TableCell align="right">{formatPct(t.retention3m)}</TableCell>
                <TableCell align="right">{formatPct(t.retention6m)}</TableCell>
                {mode === 'period' ? (
                  <TableCell align="right">
                    {t.period?.joined ?? 0} / {t.period?.left ?? 0}
                  </TableCell>
                ) : null}
                {mode === 'cohort' ? (
                  <TableCell align="right">
                    {t.cohort?.stillActive ?? 0}/{t.cohort?.size ?? 0}
                  </TableCell>
                ) : null}
                <TableCell align="right">{t.avgTenureMonths ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={!!detailTeacherId}
        onClose={() => setDetailTeacherId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {detailQuery.data?.teacher?.teacherName || 'Teacher retention'}
          <Typography variant="body2" color="text.secondary">
            Click outside or close when done. Students listed for this teacher.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detailQuery.isLoading ? (
            <Box py={3} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : detailQuery.error ? (
            <Alert severity="error">Failed to load student list</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Left</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="right">Tenure (mo)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detailQuery.data?.students || []).map((s) => (
                    <TableRow key={s.assignmentId}>
                      <TableCell>{s.studentName}</TableCell>
                      <TableCell>{s.batchName}</TableCell>
                      <TableCell>{s.joinDate}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={s.isActive ? 'Active' : 'Left'}
                          color={s.isActive ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{s.leaveDate || '—'}</TableCell>
                      <TableCell>{leaveReasonLabel(s.leaveReason)}</TableCell>
                      <TableCell align="right">{s.tenureMonths}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button onClick={() => setDetailTeacherId(null)}>Close</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function MonthlyTableTab({ month, setMonth, type, filterDate }) {
  const queryKey = `reportMonthly${type}`;
  const fetcher = {
    teachers: reportsAPI.getMonthlyTeacherPerformance,
    batches: reportsAPI.getMonthlyBatchPerformance,
    students: reportsAPI.getMonthlyStudentAttendance,
    demos: reportsAPI.getMonthlyDemos,
  }[type];

  const { data, isLoading, error } = useQuery(
    [queryKey, month],
    async () => {
      const res = await fetcher({ month });
      return res.data?.data;
    },
    { keepPreviousData: true }
  );

  if (isLoading && !data) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">Failed to load report</Alert>;

  const shiftMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => shiftMonth(-1)} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="body2" fontWeight={600}>
          Month: {data.month} (IST)
        </Typography>
        <IconButton onClick={() => shiftMonth(1)} size="small">
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      {type === 'teachers' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Teacher</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action needed</TableCell>
                <TableCell>Attendance</TableCell>
                <TableCell>Homework</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.teachers || []).map((t) => (
                <TableRow key={t.teacherId}>
                  <TableCell>{t.teacherName}</TableCell>
                  <TableCell>
                    <HealthStatusChip status={t.status} />
                  </TableCell>
                  <TableCell>{t.actionNeeded}</TableCell>
                  <TableCell>{t.attendanceUpdated}</TableCell>
                  <TableCell>{t.homeworkSent}</TableCell>
                  <TableCell>{t.classNotesCompleted}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {type === 'batches' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Batch</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Students</TableCell>
                <TableCell>Classes</TableCell>
                <TableCell>Work complete</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.batches || []).map((b) => (
                <TableRow key={b.batchId}>
                  <TableCell>{b.batchName}</TableCell>
                  <TableCell>{b.teacherName}</TableCell>
                  <TableCell>{b.studentCount}</TableCell>
                  <TableCell>{b.classesLabel}</TableCell>
                  <TableCell>{b.workCompleteLabel}</TableCell>
                  <TableCell>
                    <HealthStatusChip status={b.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {type === 'students' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Student</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Present</TableCell>
                <TableCell>Absent</TableCell>
                <TableCell>Unmarked</TableCell>
                <TableCell>Rate</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.students || []).map((s) => (
                <TableRow key={`${s.student_id}-${s.batch_name}`}>
                  <TableCell>{s.student_name}</TableCell>
                  <TableCell>{s.batch_name}</TableCell>
                  <TableCell>{s.present}</TableCell>
                  <TableCell>{s.absent}</TableCell>
                  <TableCell>{s.unmarked}</TableCell>
                  <TableCell>{s.attendanceRate != null ? `${s.attendanceRate}%` : '—'}</TableCell>
                  <TableCell>
                    <HealthStatusChip status={s.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {type === 'demos' && (() => {
        const demoItems = filterDate
          ? (data.items || []).filter((d) => String(d.scheduled_date).slice(0, 10) === filterDate)
          : data.items || [];
        const demoSummary = {
          demos: demoItems.filter((r) => r.type === 'demo').length,
          trials: demoItems.filter((r) => r.type === 'trial').length,
          completed: demoItems.filter((r) => r.status === 'completed').length,
        };
        return (
        <>
          {filterDate ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Demos &amp; trials on {filterDate}.
            </Typography>
          ) : null}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
            <SummaryCard label="Demos" value={demoSummary.demos} />
            <SummaryCard label="Trials" value={demoSummary.trials} />
            <SummaryCard label="Completed" value={demoSummary.completed} />
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Teacher</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Leads</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {demoItems.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.scheduled_date}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{d.type}</TableCell>
                    <TableCell>{d.teacher_name}</TableCell>
                    <TableCell>{d.status}</TableCell>
                    <TableCell>{d.lead_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
        );
      })()}
    </Box>
  );
}

const Reports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [mode, setMode] = useState(0);
  const [weeklyTab, setWeeklyTab] = useState(0);
  const [monthlyTab, setMonthlyTab] = useState(0);
  const [filterDate, setFilterDate] = useState('');
  const [filterTeacherId, setFilterTeacherId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const teacherPicker = useSupervisorTeacherPicker({ includeAdmin: true });

  const periodQuery = useQuery('reportCurrentPeriod', async () => {
    const res = await reportsAPI.getCurrentPeriod();
    return res.data?.data;
  });

  const [weekStart, setWeekStartRaw] = useState(null);
  const [month, setMonthRaw] = useState(null);

  const setWeekStart = useCallback((nextWeek) => {
    setWeekStartRaw(nextWeek);
    setFilterDate((prev) => {
      if (!prev || !nextWeek) return prev;
      const weekEnd = weekEndForStart(nextWeek);
      return isDateInWeek(prev, nextWeek, weekEnd) ? prev : '';
    });
  }, []);

  const setMonth = useCallback((nextMonth) => {
    setMonthRaw(nextMonth);
    setFilterDate((prev) => {
      if (!prev || !nextMonth) return prev;
      return prev.slice(0, 7) === nextMonth ? prev : '';
    });
  }, []);

  const handleFilterDateChange = useCallback(
    (date) => {
      setFilterDate(date);
      if (!date) return;
      if (mode === 0) {
        setWeekStartRaw(weekMondayForDate(date));
      } else {
        setMonthRaw(date.slice(0, 7));
      }
    },
    [mode]
  );

  React.useEffect(() => {
    if (periodQuery.data && !weekStart) setWeekStartRaw(periodQuery.data.currentWeek?.weekStart);
    if (periodQuery.data && !month) setMonthRaw(periodQuery.data.currentMonth);
  }, [periodQuery.data, weekStart, month]);

  const filterProps = {
    filterDate,
    filterTeacherId,
    filterStatus,
  };

  if (periodQuery.isLoading || !weekStart || !month) {
    return (
      <Container maxWidth="xl">
        <Box py={6} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Health scorecards for teachers and batches. Week runs Monday–Sunday (IST). Problem = 3+ incomplete
          sessions.
        </Typography>
        {isAdmin && (
          <Button variant="outlined" size="small" sx={{ mt: 2 }} onClick={() => navigate('/management')}>
            Open Sales &amp; Enrollment dashboard
          </Button>
        )}
      </Box>

      <Tabs value={mode} onChange={(_, v) => setMode(v)} sx={{ mb: 2 }}>
        <Tab label="Weekly" />
        <Tab label="Monthly" />
      </Tabs>

      <ReportsFilterBar
        mode={mode}
        weeklyTab={weeklyTab}
        monthlyTab={monthlyTab}
        filterDate={filterDate}
        onFilterDateChange={handleFilterDateChange}
        filterTeacherId={filterTeacherId}
        onFilterTeacherChange={setFilterTeacherId}
        filterStatus={filterStatus}
        onFilterStatusChange={setFilterStatus}
        teachers={teacherPicker.teachers}
        teachersLoading={teacherPicker.teachersLoading}
      />

      {mode === 0 ? (
        <>
          <Tabs value={weeklyTab} onChange={(_, v) => setWeeklyTab(v)} sx={{ mb: 2 }}>
            <Tab label="Batch Health" />
            <Tab label="Teacher Health" />
            <Tab label="Class start" />
            <Tab label="Pending Items" />
          </Tabs>
          {weeklyTab === 0 && (
            <BatchHealthTab weekStart={weekStart} setWeekStart={setWeekStart} {...filterProps} />
          )}
          {weeklyTab === 1 && (
            <TeacherHealthTab weekStart={weekStart} setWeekStart={setWeekStart} {...filterProps} />
          )}
          {weeklyTab === 2 && (
            <ClassStartPunctualityTab weekStart={weekStart} setWeekStart={setWeekStart} {...filterProps} />
          )}
          {weeklyTab === 3 && (
            <PendingItemsTab weekStart={weekStart} setWeekStart={setWeekStart} filterDate={filterDate} />
          )}
        </>
      ) : (
        <>
          <Tabs value={monthlyTab} onChange={(_, v) => setMonthlyTab(v)} sx={{ mb: 2 }} variant="scrollable">
            <Tab label="Management Summary" />
            <Tab label="Teacher Performance" />
            <Tab label="Teacher Retention" />
            <Tab label="Batch Performance" />
            <Tab label="Student Attendance" />
            <Tab label="Demos & Trials" />
          </Tabs>
          {monthlyTab === 0 && <MonthlySummaryTab month={month} setMonth={setMonth} />}
          {monthlyTab === 1 && <MonthlyTableTab month={month} setMonth={setMonth} type="teachers" />}
          {monthlyTab === 2 && <TeacherRetentionTab month={month} setMonth={setMonth} />}
          {monthlyTab === 3 && <MonthlyTableTab month={month} setMonth={setMonth} type="batches" />}
          {monthlyTab === 4 && <StudentAttendanceView mode="reports" embedded />}
          {monthlyTab === 5 && (
            <MonthlyTableTab month={month} setMonth={setMonth} type="demos" filterDate={filterDate} />
          )}
        </>
      )}
    </Container>
  );
};

export default Reports;
